import React, { Fragment } from "react";
import withStyles from "isomorphic-style-loader/withStyles";
import { connect } from "react-redux";
import { Col, Row, Button } from "reactstrap";
import ModalFrame from "../../../toolkit/modalFrame";
import s from "./styles.scss";
import get from "lodash.get";
import { ScaleLoader } from "react-spinners";
import ReactTooltip from "react-tooltip";
import * as modalActions from "../../../utils/actions/modals";
import { serviceCaller, ServiceCallerProps } from "../../composition";
import { firebaseConnect } from "react-redux-firebase";
import ICharity from "../../../types/services/ICharity";
import { IPost } from "../../../types/services/IPost";
import notFound from "../../../media/img/not_found.png";
import removeMd from "remove-markdown";
import Dotdotdot from "react-dotdotdot";
import classNames from "classnames";
import {
  getParsedDate,
  getPostThumbnailDisplayUrl,
  getTargetedDonors,
  smsChannelLabel,
} from "../../../utils/helpers";
import {
  executeNotificationDetailsExport,
  getPostEmailMetrics,
  getPostSmsMetrics,
  getPostSmsUnsubscribedMetrics,
} from "../../../utils/fetch";
import {
  errorMessageMetrics,
  ISparkPostMetrics,
} from "../../../types/services/IMetrics";
import { downloadFile } from "../../../utils/downloadBlobFile";
import PostNotTargetedMetrics from "../postNotTargetedMetrics";
import SpecificContactsLabel from "../specificContactsLabel";

const METRICS_NOT_AVAILABLE_YET =
  "Metrics not available yet - check again later";

interface Props extends ServiceCallerProps {
  userId: string;
  isOpen: boolean;
  toggle: () => void;
  currentCharity: ICharity;
  currentPost: IPost;
  enableUnsubscribedMetrics: boolean;
  enableInvalidsMetrics: boolean;
  disableExtraMetrics: boolean;
  enableOnlySmsPost: boolean;

  togglePostNotificationModal: (post: IPost) => void;
}

interface State {
  loaded: boolean;
  sparkPostMetrics: ISparkPostMetrics;
  emailUnsubscribedMetrics: { [key: string]: number };
  simpleTextingMetrics: { [key: string]: number };
  smsUnsubscribedMetrics: { [key: string]: number };
  loadingExport: boolean;
  notificationsTimestamp: number;
  /** Auxiliary modal: metric glossary for MMS vs Email section */
  metricsHelpModal: null | "sms" | "email";
}

class PostNotificationDetails extends React.Component<Props, State> {
  state = {
    loaded: false,
    sparkPostMetrics: {} as ISparkPostMetrics,
    emailUnsubscribedMetrics: {},
    simpleTextingMetrics: {},
    smsUnsubscribedMetrics: {},
    loadingExport: false,
    notificationsTimestamp: 1599526800000,
    metricsHelpModal: null,
  };

  closeMetricsHelpModal = () => {
    this.setState({ metricsHelpModal: null });
  };

  UNSAFE_componentWillUpdate(nextProps) {
    if (this.props.isOpen && !nextProps.isOpen) {
      this.setState({ loaded: false, metricsHelpModal: null });
    }
  }

  componentDidMount(): void {
    this.loadMetrics();
  }

  componentDidUpdate(prevProps: Props): void {
    const modalOpened = !prevProps.isOpen && this.props.isOpen;
    const postChangedWhileOpen =
      this.props.isOpen &&
      prevProps.currentPost?.id !== this.props.currentPost?.id &&
      !!this.props.currentPost?.id;

    if (modalOpened || postChangedWhileOpen) {
      this.loadMetrics();
    }
  }

  loadMetrics = (): void => {
    if (!this.props.isOpen || !this.props.currentPost?.id) {
      return;
    }

    this.setState({ loaded: false });

    this.getPostMetrics()
      .then(responses => {
        this.setState({
          loaded: true,
          sparkPostMetrics: responses[0],
          simpleTextingMetrics: responses[1],
          smsUnsubscribedMetrics: responses[2],
          emailUnsubscribedMetrics: responses[3],
        });
      })
      .catch(() => {
        this.setState({
          loaded: true,
          sparkPostMetrics: {} as ISparkPostMetrics,
          simpleTextingMetrics: {},
          smsUnsubscribedMetrics: {},
          emailUnsubscribedMetrics: {},
        });
      });
  };

  getPostMetrics = () => {
    let promises = [];
    promises.push(
      getPostEmailMetrics(
        this.props.config.searchServiceUrl,
        this.props.currentCharity.id,
        this.props.currentPost.id,
        this.props.serviceAccessToken
      )
    );
    promises.push(
      getPostSmsMetrics(
        this.props.config.searchServiceUrl,
        this.props.currentCharity.id,
        this.props.currentPost.id,
        this.props.serviceAccessToken,
        this.props.currentPost.shortUrl ? this.props.currentPost.shortUrl : ""
      )
    );
    promises.push(
      getPostSmsUnsubscribedMetrics(
        this.props.config.searchServiceUrl,
        this.props.currentCharity.id,
        this.props.currentPost.id,
        this.props.serviceAccessToken,
        "SMS"
      )
    );
    promises.push(
      getPostSmsUnsubscribedMetrics(
        this.props.config.searchServiceUrl,
        this.props.currentCharity.id,
        this.props.currentPost.id,
        this.props.serviceAccessToken,
        "EMAIL"
      )
    );
    return Promise.all(promises).then(promises => promises);
  };

  getMediaUrl(post: IPost): string {
    return getPostThumbnailDisplayUrl(post);
  }

  getPostTitle(post: IPost): string {
    return post ? post.title : "Not Available";
  }

  getSegmentCountForId = (
    post: IPost,
    segId: string
  ): number | "unavailable" => {
    const sc = post.segmentsCount;
    if (!sc) {
      return "unavailable";
    }
    const v = sc[segId];
    if (typeof v !== "number" || v < 0) {
      return "unavailable";
    }
    return v;
  };

  sumSegmentKeysTotal = (post: IPost, keys: string[]): number | null => {
    if (!keys.length) {
      return 0;
    }
    let sum = 0;
    for (const id of keys) {
      const v = this.getSegmentCountForId(post, id);
      if (v === "unavailable") {
        return null;
      }
      sum += v;
    }
    return sum;
  };

  renderTotalSelectedModal = (post: IPost) => {
    const incKeys = Object.keys(post.segments || {});
    const excKeys = Object.keys(post.excludeSegments || {});
    if (incKeys.length === 0 && excKeys.length === 0) {
      return null;
    }
    const included = this.sumSegmentKeysTotal(post, incKeys);
    const excluded = this.sumSegmentKeysTotal(post, excKeys);
    const specificCount = Object.keys(post.donors || {}).length;
    const excludeDonorCount = Object.keys(post.excludeDonors || {}).length;
    const selected =
      incKeys.length === 0 && excKeys.length > 0
        ? null
        : included !== null && excluded !== null
        ? included + specificCount - (excluded + excludeDonorCount)
        : null;

    if (selected === null) {
      return null;
    }

    const tipId = `tooltip-total-selected-modal-${post.id || "post"}`;

    return (
      <p className={s.date}>
        <strong>Total selected contacts:</strong> {selected}{" "}
        <span data-tip data-for={tipId}>
          <i className="fa fa-question-circle" />
        </span>
        <ReactTooltip
          id={tipId}
          type="info"
          place="bottom"
          effect="float"
          delayHide={500}
        >
          Preliminary estimate; final audience is often lower after validation,
          deduplication, and removing invalid contacts.
        </ReactTooltip>
      </p>
    );
  };

  getCounts = (post: IPost) => {
    const smsLabel = smsChannelLabel(post?.postType);
    const counts = post && post.notificationsCount;
    const notTargeted = post ? (
      <PostNotTargetedMetrics
        lineClassName={s.date}
        notTargeted={counts?.notTargeted}
        charityId={this.props.currentCharity?.id}
        postId={post.id}
        searchUrl={this.props.config?.searchServiceUrl}
        token={this.props.serviceAccessToken}
        tooltipId={`post-details-${post.id || "post"}`}
        strongLabels
      />
    ) : null;
    if (post && counts) {
      return (
        <Fragment>
          <p className={s.date}>
            <strong>Target Metrics</strong>
          </p>
          <p className={s.date}>
            <strong>{smsLabel}:</strong>{" "}
            {typeof counts.sms !== "undefined" ? counts.sms : "Off"}
          </p>
          <p className={s.date}>
            <strong>Email:</strong>{" "}
            {typeof counts.email !== "undefined" ? counts.email : "Off"}
          </p>
          {notTargeted}
        </Fragment>
      );
    }
    return (
      <Fragment>
        <p className={s.date}>
          <strong>Target Metrics</strong>
        </p>
        <p className={s.date}>
          <strong>{smsLabel}:</strong> Off
        </p>
        <p className={s.date}>
          <strong>Email:</strong> Off
        </p>
        {notTargeted}
      </Fragment>
    );
  };

  getEmailNotificationDetails = () => {
    let details = [];
    const post = this.props.currentPost;

    if (post && post.notifications && !post.notifications.email) {
      details.push(
        <p key={"email-off"} className={s.detailEntry}>
          Email notifications are off for this post.
        </p>
      );
      return details;
    }

    const emailTargeted =
      post &&
      post.notificationsCount &&
      typeof post.notificationsCount.email === "number"
        ? post.notificationsCount.email
        : null;

    if (emailTargeted === 0) {
      details.push(
        <p key={"email-none"} className={s.detailEntry}>
          This post didn&apos;t target any email donors.
        </p>
      );
      return details;
    }

    const countSent = this.state.sparkPostMetrics.count_sent;
    if (countSent === 0 || countSent == null) {
      details.push(
        <p key={"not-yet-email"} className={s.detailEntry}>
          {METRICS_NOT_AVAILABLE_YET}
        </p>
      );
      return details;
    }

    let deliveredEmails = this.state.sparkPostMetrics.count_delivered;
    let unsubscribedCount = get(
      this.state.emailUnsubscribedMetrics,
      "UNSUBSCRIBED_TOTAL",
      0
    );
    unsubscribedCount =
      unsubscribedCount > deliveredEmails ? deliveredEmails : unsubscribedCount;
    details.push(
      <p key={"Delivered"} className={s.detailEntry}>
        <strong>Delivered Emails:</strong> {deliveredEmails}
      </p>
    );
    if (!this.props.disableExtraMetrics) {
      details.push(
        <p key={"Invalid_Emails"} className={s.detailEntry}>
          <strong>Invalid Emails:</strong>{" "}
          {get(this.state.sparkPostMetrics, "count_bounce", 0) +
            get(this.state.sparkPostMetrics, "count_policy_rejection", 0)}
        </p>
      );
    }
    details.push(
      <p key={"Clicks"} className={s.detailEntry}>
        <strong>Unique Clicks:</strong>{" "}
        {this.state.sparkPostMetrics.count_unique_clicked}
      </p>
    );
    details.push(
      <p key={"Clicks_Rate"} className={s.detailEntry}>
        <strong>Click Rate:</strong>{" "}
        {(
          (this.state.sparkPostMetrics.count_delivered
            ? this.state.sparkPostMetrics.count_unique_clicked /
              this.state.sparkPostMetrics.count_delivered
            : 0) * 100
        ).toFixed(2) + "%"}
      </p>
    );
    details.push(
      <p key={"Open_Rate"} className={s.detailEntry}>
        <strong>Open Rate:</strong>{" "}
        {(
          (this.state.sparkPostMetrics.count_unique_confirmed_opened
            ? this.state.sparkPostMetrics.count_unique_confirmed_opened /
              this.state.sparkPostMetrics.count_delivered
            : 0) * 100
        ).toFixed(2) + "%"}
      </p>
    );
    if (this.props.enableUnsubscribedMetrics) {
      details.push(
        <p key={"Unsubscribed"} className={s.detailEntry}>
          <strong>Unsubscribed Count:</strong>{" "}
          {deliveredEmails ? unsubscribedCount : 0}
        </p>
      );
      details.push(
        <p key={"Unsubscribed_Rate"} className={s.detailEntry}>
          <strong>Unsubscribed Rate:</strong>{" "}
          {(
            (deliveredEmails ? unsubscribedCount / deliveredEmails : 0) * 100
          ).toFixed(2) + "%"}
        </p>
      );
    }
    return details;
  };

  getSmsNotificationDetails = () => {
    let details = [];
    const post = this.props.currentPost;

    if (post && post.notifications && !post.notifications.sms) {
      details.push(
        <p key={"sms-off"} className={s.detailEntry}>
          MMS notifications are off for this post.
        </p>
      );
      return details;
    }

    const smsTargeted =
      post &&
      post.notificationsCount &&
      typeof post.notificationsCount.sms === "number"
        ? post.notificationsCount.sms
        : null;

    if (smsTargeted === 0) {
      details.push(
        <p key={"sms-none"} className={s.detailEntry}>
          This post didn&apos;t target any MMS donors.
        </p>
      );
      return details;
    }

    if (Object.keys(this.state.simpleTextingMetrics).length == 1) {
      details.push(
        <p key={"not-yet-sms"} className={s.detailEntry}>
          {METRICS_NOT_AVAILABLE_YET}
        </p>
      );
      return details;
    }
    let success = get(this.state.simpleTextingMetrics, "SUCCESS", 0);
    let unsubscribedCount = get(
      this.state.smsUnsubscribedMetrics,
      "UNSUBSCRIBED_TOTAL",
      0
    );
    unsubscribedCount =
      unsubscribedCount > success ? success : unsubscribedCount;

    details.push(
      <p key={"SENT"} className={s.detailEntry}>
        <strong>{errorMessageMetrics("SENT")}:</strong> {success}
      </p>
    );
    if (!this.props.disableExtraMetrics || this.props.enableInvalidsMetrics) {
      details.push(
        <p key={"INVALIDS"} className={s.detailEntry}>
          <strong>{errorMessageMetrics("INVALIDS")}:</strong>{" "}
          {get(this.state.simpleTextingMetrics, "INVALID_CONTACT", 0) +
            get(this.state.simpleTextingMetrics, "INVALID_NUMBER", 0)}
        </p>
      );
    }
    details.push(
      <p key={"TOTAL_CLICKS"} className={s.detailEntry}>
        <strong>{errorMessageMetrics("TOTAL_CLICKS")}:</strong>{" "}
        {this.props.currentPost && !this.props.currentPost.shortUrl
          ? "ViewSpark without URL"
          : get(
              this.state.simpleTextingMetrics,
              "TOTAL_CLICKS",
              get(this.props.currentPost, "clickCount", 0)
            )}
      </p>
    );
    details.push(
      <p key={"CLICKS_RATE"} className={s.detailEntry}>
        <strong>{errorMessageMetrics("CLICKS_RATE")}:</strong>{" "}
        {this.props.currentPost && !this.props.currentPost.shortUrl
          ? "ViewSpark without URL"
          : (
              (get(this.state.simpleTextingMetrics, "SUCCESS", 0) !== 0
                ? get(this.state.simpleTextingMetrics, "TOTAL_CLICKS", 0) /
                  get(this.state.simpleTextingMetrics, "SUCCESS", 0)
                : 0) * 100
            ).toFixed(2) + "%"}
      </p>
    );
    if (this.props.enableUnsubscribedMetrics) {
      details.push(
        <p key={"UNSUBSCRIBED_TOTAL"} className={s.detailEntry}>
          <strong>{errorMessageMetrics("UNSUBSCRIBED_TOTAL")}:</strong>{" "}
          {success !== 0 ? unsubscribedCount : 0}
        </p>
      );
      details.push(
        <p key={"UNSUBSCRIBED_RATE"} className={s.detailEntry}>
          <strong>{errorMessageMetrics("UNSUBSCRIBED_RATE")}:</strong>{" "}
          {((success !== 0 ? unsubscribedCount / success : 0) * 100).toFixed(
            2
          ) + "%"}
        </p>
      );
    } /*else if (!this.props.disableExtraMetrics) {
      details.push(
        <p key={"UNSUBSCRIBED_TOTAL"} className={s.detailEntry}>
          {errorMessageMetrics("UNSUBSCRIBED_TOTAL") +
            ": " +
            get(this.state.simpleTextingMetrics, "OPT_OUT", 0)}
        </p>
      );
    }*/
    if (get(this.state.simpleTextingMetrics, "INVALID_TEXT_MESSAGE", 0) !== 0) {
      details.push(
        <p key={"INVALID_TEXT_MESSAGE"} className={s.detailEntry}>
          <strong>{errorMessageMetrics("INVALID_TEXT_MESSAGE")}:</strong>{" "}
          {get(this.state.simpleTextingMetrics, "INVALID_TEXT_MESSAGE", 0)}
        </p>
      );
    }

    let pendingStatus = get(
      this.state.simpleTextingMetrics,
      "PENDING_STATUS",
      0
    );
    if (pendingStatus !== 0) {
      details.push(
        <p key={"PENDING_STATUS"} className={s.detailEntry}>
          <strong>{errorMessageMetrics("PENDING_STATUS")}:</strong>{" "}
          {pendingStatus}
        </p>
      );
    }

    // let count = 0;
    // Object.keys(this.state.simpleTextingMetrics).forEach(key => {
    //   if (errorMessageMetrics(key) === "Unknown Error Occurred") {
    //     count += parseInt(this.state.simpleTextingMetrics[key]);
    //   }
    // });
    // if (count > 0) {
    //   details.push(
    //     <p key={"UNKNOWN"} className={s.detailEntry}>
    //       {errorMessageMetrics("UNKNOWN_ERROR") + ": " + count}
    //     </p>
    //   );
    // }
    return details;
  };

  renderMmsMetricsHelpBody = () => {
    return (
      <div className={s.metricsHelpModalBody}>
        <ul className={classNames(s.tooltip, s.metricsHelpDefList)}>
          <li>
            <strong>Success:</strong> MMS delivered successfully.
          </li>
          {(!this.props.disableExtraMetrics ||
            this.props.enableInvalidsMetrics) && (
            <li>
              <strong>Invalids:</strong> MMS tried to be delivered to a bad
              format number, invalid contact or previously opt out contact.
            </li>
          )}
          <li>
            <strong>Total Clicks:</strong> Number of clicks the link have
            received.
          </li>
          <li>
            <strong>Click Rate:</strong> Percentage of clicks over successful
            MMS.
          </li>
          <li>
            <strong>Invalid Text Length:</strong> The MMS length was more than
            160 characters.
          </li>
          {this.props.enableUnsubscribedMetrics && (
            <Fragment>
              <li>
                <strong>Unsubscribed Count:</strong> Number of opt out donors in
                a week period after post publication.
              </li>
              <li>
                <strong>Unsubscribed Rate:</strong> Percentage of MMS
                unsubscribe donors for post.
              </li>
            </Fragment>
          )}
        </ul>
      </div>
    );
  };

  renderEmailMetricsHelpBody = () => {
    return (
      <div className={s.metricsHelpModalBody}>
        <ul className={classNames(s.tooltip, s.metricsHelpDefList)}>
          <li>
            <strong>Delivered Emails:</strong> Number of emails that were
            received by a recipient&apos;s mailbox provider.
          </li>
          <li>
            <strong>Invalid Emails:</strong> Emails tried to be delivered to an
            invalid address or a blocked domain.
          </li>
          <li>
            <strong>Unique Clicks:</strong> Number of emails that had at least
            one link clicked.
          </li>
          <li>
            <strong>Click Rate:</strong> Percentage of clicks over delivered
            Emails.
          </li>
          <li>
            <strong>Open Rate:</strong> Number of opened emails divided
            Delivered quantity of emails.
          </li>
          {this.props.enableUnsubscribedMetrics && (
            <Fragment>
              <li>
                <strong>Unsubscribed Count:</strong> Number of opt out donors in
                a week period after post publication.
              </li>
              <li>
                <strong>Unsubscribed Rate:</strong> Unsubscribed Count divided
                Delivery quantity of email.
              </li>
            </Fragment>
          )}
        </ul>
      </div>
    );
  };

  exportDetails = () => {
    this.setState(
      {
        loadingExport: true,
      },
      () => {
        executeNotificationDetailsExport(
          this.props.config.searchServiceUrl,
          this.props.currentCharity.id,
          this.props.serviceAccessToken,
          this.props.currentPost.id
        ).then(blob => {
          downloadFile(
            blob,
            `Notification Details_${this.props.currentPost.title}.csv`
          );
          this.setState({
            loadingExport: false,
          });
        });
      }
    );
  };

  render() {
    const { currentPost } = this.props;
    let mediaUrl = this.getMediaUrl(currentPost);
    let title = this.getPostTitle(currentPost);

    const showTargetedContacts =
      currentPost &&
      currentPost.targetedDonors !== null &&
      typeof currentPost.targetedDonors !== "undefined" &&
      currentPost.targetedDonors >= 0;

    let specificDonors =
      currentPost &&
      currentPost.donors &&
      Object.keys(currentPost.donors).length > 0;
    let excludedDonors =
      currentPost &&
      currentPost.excludeDonors &&
      Object.keys(currentPost.excludeDonors).length > 0;
    let segments =
      currentPost &&
      currentPost.segments &&
      Object.keys(currentPost.segments).length > 0;
    let excludedSegments =
      currentPost &&
      currentPost.excludeSegments &&
      Object.keys(currentPost.excludeSegments).length > 0;

    const smsLabel = smsChannelLabel(currentPost?.postType);

    return (
      <Fragment>
        <ModalFrame
          title={`ViewSpark Engagement Summary`}
          subtitle=""
          isOpen={this.props.isOpen}
          toggle={this.props.toggle}
          size="l"
          className={s["modal-l"]}
        >
          <div className={s.centered}>
            <ScaleLoader color={"#1f65b1"} loading={!this.state.loaded} />
          </div>

          {this.state.loaded && (
            <Fragment>
              <Row className={classNames(s.firstRow, s.extraMargin)}>
                <Col
                  xs={3}
                  md={3}
                  className={classNames(s.colMargin, s.notificationsContent)}
                >
                  <div className={s["img-container"]}>
                    <img
                      className={s.img}
                      data-elm-id="img_logo"
                      src={mediaUrl !== "" ? mediaUrl : notFound}
                    />
                  </div>
                </Col>
                <Col xs={9} md={9} className={s.colMargin}>
                  <div className={s.content}>
                    <Dotdotdot clamp={"auto"}>
                      <h6>{title}</h6>
                    </Dotdotdot>
                    <Dotdotdot clamp={4}>
                      <p>
                        {removeMd(currentPost ? currentPost.content : "") ||
                          "Not available"}
                      </p>
                    </Dotdotdot>
                  </div>
                </Col>
              </Row>
              <Row className={classNames(s.row, s.extraMargin)}>
                <Col
                  xs={6}
                  md={6}
                  className={classNames(s.colMargin, s.notificationsContent)}
                >
                  {currentPost && this.renderTotalSelectedModal(currentPost)}
                  <p className={s.date}>
                    <strong>Created:</strong>{" "}
                    {currentPost && currentPost.createdOn
                      ? getParsedDate(
                          new Date(currentPost.createdOn).toString(),
                          false,
                          "MM/DD/YYYY, hh:mm a"
                        )
                      : "Not available"}
                  </p>
                  {currentPost &&
                    currentPost.status &&
                    currentPost.status !== "draft" && (
                      <p className={s.date}>
                        <strong>Published:</strong>{" "}
                        {currentPost && currentPost.scheduleTime
                          ? getParsedDate(
                              new Date(currentPost.scheduleTime).toString(),
                              false,
                              "MM/DD/YYYY, hh:mm a"
                            )
                          : "Not available"}
                      </p>
                    )}
                  {this.props.enableOnlySmsPost && currentPost && (
                    <p className={s.date}>
                      <strong>Message Type:</strong>{" "}
                      {smsChannelLabel(currentPost.postType)}
                    </p>
                  )}
                </Col>
                <Col
                  xs={6}
                  md={6}
                  className={classNames(s.colMargin, s.notificationsContent)}
                >
                  {currentPost && currentPost.motivationCode && (
                    <Fragment>
                      <p className={s.date}>
                        <strong>Motivation Code:</strong>{" "}
                        {this.props.currentPost.motivationCode}
                      </p>
                    </Fragment>
                  )}
                  {!specificDonors &&
                    !excludedDonors &&
                    !segments &&
                    !excludedSegments && (
                      <p className={s.date}>
                        <strong>All Contacts</strong>
                      </p>
                    )}
                  {specificDonors && (
                    <SpecificContactsLabel
                      lineClassName={s.date}
                      donorIds={Object.keys(currentPost.donors || {})}
                      charityId={this.props.currentCharity?.id}
                      searchUrl={this.props.config?.searchServiceUrl}
                      token={this.props.serviceAccessToken}
                      tooltipId={`specific-contacts-modal-${currentPost.id ||
                        "post"}`}
                      strongLabel
                    />
                  )}
                  {showTargetedContacts && currentPost && (
                    <p className={s.date}>
                      <strong>Targeted Contacts:</strong>{" "}
                      {getTargetedDonors(currentPost)}{" "}
                      <span
                        data-tip
                        data-for={`tooltip-targeted-contacts-modal-${currentPost.id ||
                          "post"}`}
                      >
                        <i className="fa fa-question-circle" />
                      </span>
                      <ReactTooltip
                        id={`tooltip-targeted-contacts-modal-${currentPost.id ||
                          "post"}`}
                        type="info"
                        place="bottom"
                        effect="float"
                        delayHide={500}
                      >
                        This is the total number of contacts we sent this post.
                        It can differ from the total selected count.
                      </ReactTooltip>
                    </p>
                  )}
                  {this.getCounts(currentPost)}
                </Col>
              </Row>
              <Row className={s.extraMargin}>
                <Col xs={6} className={s.colMargin + " " + s.divRowSms}>
                  <Row className={s.titleRow}>
                    <div className={s.detailsContent}>
                      <div className={s.metricsHeaderRow}>
                        <h5 className={s.metricsTitle}>
                          <strong>{smsLabel}</strong>
                        </h5>
                        <button
                          type="button"
                          className={s.metricsHelpBtn}
                          aria-label={`More information about ${smsLabel} metrics`}
                          data-tip
                          data-for="tp-mms-metrics-help"
                          onClick={() =>
                            this.setState({ metricsHelpModal: "sms" })
                          }
                        >
                          ?
                        </button>
                        <ReactTooltip
                          id="tp-mms-metrics-help"
                          type="info"
                          place="bottom"
                          effect="float"
                        >
                          <span>Click for more info</span>
                        </ReactTooltip>
                      </div>
                      <Fragment>{this.getSmsNotificationDetails()}</Fragment>
                    </div>
                  </Row>
                </Col>
                <Col xs={6} className={s.colMargin + " " + s.divRowEmail}>
                  <Row className={s.titleRow}>
                    <div className={s.detailsContent}>
                      <div className={s.metricsHeaderRow}>
                        <h5 className={s.metricsTitle}>
                          <strong>Email</strong>
                        </h5>
                        <button
                          type="button"
                          className={s.metricsHelpBtn}
                          aria-label="More information about Email metrics"
                          data-tip
                          data-for="tp-email-metrics-help"
                          onClick={() =>
                            this.setState({ metricsHelpModal: "email" })
                          }
                        >
                          ?
                        </button>
                        <ReactTooltip
                          id="tp-email-metrics-help"
                          type="info"
                          place="bottom"
                          effect="float"
                        >
                          <span>Click for more info</span>
                        </ReactTooltip>
                      </div>
                      <Fragment>{this.getEmailNotificationDetails()}</Fragment>
                    </div>
                  </Row>
                </Col>
              </Row>
              {this.props.currentPost &&
                this.props.currentPost.scheduleTime >
                  this.state.notificationsTimestamp && (
                  <Row className={s.extraMargin}>
                    <Col xs={12}>
                      <Button
                        data-tip
                        data-for={`tp-button-export`}
                        onClick={this.exportDetails}
                        className={s.button}
                        disabled={this.state.loadingExport}
                      >
                        Donor Notification Status
                      </Button>
                      <ReactTooltip
                        id={`tp-button-export`}
                        type="info"
                        place="bottom"
                        effect="float"
                        show={true}
                      >
                        <span>
                          Accurate information will be available 24 hours after
                          publication.
                        </span>
                      </ReactTooltip>
                    </Col>
                  </Row>
                )}
            </Fragment>
          )}
        </ModalFrame>
        <ModalFrame
          title={
            this.state.metricsHelpModal === "email"
              ? "Email Metrics Definitions"
              : this.state.metricsHelpModal === "sms"
              ? `${smsLabel} Metrics Definitions`
              : ""
          }
          subtitle=""
          isOpen={this.state.metricsHelpModal !== null}
          toggle={this.closeMetricsHelpModal}
          className={s.metricsHelpModal}
        >
          {this.state.metricsHelpModal === "sms" &&
            this.renderMmsMetricsHelpBody()}
          {this.state.metricsHelpModal === "email" &&
            this.renderEmailMetricsHelpBody()}
        </ModalFrame>
      </Fragment>
    );
  }
}

const mapStateToProps = state => {
  const { modals, user, firebase } = state;
  const enableUnsubscribedMetrics = get(
    firebase,
    `data.${user.currentCharity.id}.config.enableUnsubscribedMetrics`
  );
  const enableInvalidsMetrics = get(
    firebase,
    `data.${user.currentCharity.id}.config.enableInvalidsMetrics`
  );
  const disableExtraMetrics = get(
    firebase,
    `data.${user.currentCharity.id}.config.disableExtraMetrics`
  );
  const enableOnlySmsPost = get(
    firebase,
    `data.${user.currentCharity.id}.config.enableOnlySmsPost`,
    false
  );
  return {
    currentPost: modals.post,
    currentCharity: user.currentCharity,
    enableUnsubscribedMetrics: enableUnsubscribedMetrics,
    enableInvalidsMetrics: enableInvalidsMetrics,
    disableExtraMetrics: disableExtraMetrics,
    enableOnlySmsPost: Boolean(enableOnlySmsPost),
  };
};

const mapDispatchToProps = dispatch => ({
  toggleUserProfileModal: (post = null) => {
    dispatch(modalActions.togglePostNotificationModal(post));
  },
});

export default serviceCaller(
  connect<Props>(
    mapStateToProps,
    mapDispatchToProps
  )(
    firebaseConnect((_, store) => {
      return store.getState().user.currentCharity.id !== ""
        ? [
            `${
              store.getState().user.currentCharity.id
            }/config/enableUnsubscribedMetrics`,
            `${
              store.getState().user.currentCharity.id
            }/config/enableInvalidsMetrics`,
            `${
              store.getState().user.currentCharity.id
            }/config/disableExtraMetrics`,
            `${
              store.getState().user.currentCharity.id
            }/config/enableOnlySmsPost`,
          ]
        : [];
    })(withStyles(s)(PostNotificationDetails))
  )
);
